/**
 * connectSignedInEvent — ONE announcement that a browser-lane sign-in
 * finished (2026-09-03 owner report: "after signing in it came back to the
 * chat but nothing occurred").
 *
 * THE DEFECT THIS EXISTS TO CLOSE
 * -------------------------------
 * `DesktopSignInModal` reported completion through an `onConnected` PROP. The
 * Integrations page passed one; `DesktopSignInModalHost` — the mount every
 * chat, onboarding and card-driven sign-in actually uses — did not pass one at
 * all. So the most common path in the product finished a sign-in and told
 * nobody: the surface closed, the thread that had asked the user to sign in
 * sat exactly where it was, and the original question ("how are my health
 * vitals going?") was simply abandoned mid-answer.
 *
 * A prop can only reach the code that mounted the component. This is a
 * document event, so the surfaces that need to know — the thread that started
 * it, the Integrations page behind it, anything added later — hear the same
 * thing at the same moment, whichever mount was used.
 *
 * The ORIGIN is the other half. A sign-in started from a chat card carries the
 * thread it came from and the question that was interrupted, so the thread can
 * be resumed rather than merely notified. Surfaces with no origin (the
 * Integrations page) simply omit it.
 */

export const CONNECT_SIGNED_IN_EVENT = 'prest0n:connect-signed-in';

/** Where a sign-in came from, when it came from a conversation. */
export interface ConnectSignInOrigin {
  /** The thread that asked for the connection. */
  threadId: string;
  /**
   * What the user actually wanted, in their own words — the question the
   * connect card interrupted. Prest0n resumes THIS, rather than asking the
   * user to repeat themselves.
   */
  resumeText?: string;
}

export interface ConnectSignedInDetail {
  appName: string;
  /** The custom-connection slug the sign-in was saved onto, when there is one. */
  customSlug?: string;
  /** The identity the user attested, when they gave one. */
  signedInAs?: string;
  origin?: ConnectSignInOrigin;
}

/** Announce a finished sign-in. Never throws — a listener that explodes must
 *  not take down the surface that just succeeded. */
export function announceConnectSignedIn(detail: ConnectSignedInDetail): void {
  try {
    document.dispatchEvent(new CustomEvent(CONNECT_SIGNED_IN_EVENT, { detail }));
  } catch {
    /* a broadcast is never load-bearing for the connection itself */
  }
}

/** Read an origin off an untrusted event/DOM payload. Anything malformed is
 *  no origin at all — a resume into the wrong thread is worse than none. */
export function readConnectSignInOrigin(raw: unknown): ConnectSignInOrigin | undefined {
  const o = (raw || {}) as Record<string, unknown>;
  const threadId = typeof o.threadId === 'string' ? o.threadId.trim() : '';
  if (!threadId || threadId.length > 200) return undefined;
  const resumeText = typeof o.resumeText === 'string' ? o.resumeText.trim().slice(0, 2000) : '';
  return { threadId, ...(resumeText ? { resumeText } : {}) };
}
